import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from './AuthProvider';
import { supabase } from '@/lib/supabase';

interface SiteContextType {
  siteId: string | null;
  switching: boolean;
  switchSite: (siteId: string) => Promise<void>;
}

const SiteContext = createContext<SiteContextType | undefined>(undefined);

export function SiteProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [switching, setSwitching] = useState(false);

  // current_site_id() no Postgres lê app_metadata.site_id do JWT — então o
  // site "ativo" no frontend precisa vir exatamente dali, não de uma query
  // separada em site_members (que teria RLS escopada pelo próprio site_id
  // ainda não definido).
  const siteId = useMemo(() => (user?.app_metadata?.site_id as string | undefined) ?? null, [user]);

  const switchSite = async (newSiteId: string) => {
    setSwitching(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (!accessToken) throw new Error('Sessão inválida.');

      const { error } = (await supabase.functions.invoke('switch-site', {
        body: { site_id: newSiteId },
      })) as { data: unknown; error: Error | null };
      if (error) throw error;

      // app_metadata só é atualizado no JWT em uma nova emissão de token.
      const { error: refreshError } = await supabase.auth.refreshSession();
      if (refreshError) throw refreshError;
    } finally {
      setSwitching(false);
    }
  };

  return (
    <SiteContext.Provider value={{ siteId, switching, switchSite }}>
      {children}
    </SiteContext.Provider>
  );
}

export function useSite() {
  const context = useContext(SiteContext);
  if (context === undefined) {
    throw new Error('useSite deve ser usado dentro de um SiteProvider');
  }
  return context;
}
